import { Controller } from "@hotwired/stimulus";
import { TabulatorFull as Tabulator } from "tabulator-tables";

function createActionButton(action, args, cell, controller) {
  const button = document.createElement("a");

  button.className = `${action}-btn`;
  button.setAttribute("title", action);

  if (args.action) {
    button.setAttribute("data-action", args.action);
  } else if (args.url) {
    const url = args.url.replace(":id", cell.getRow().getData().id);
    button.href = url;
    button.setAttribute("data-turbo-stream", "");
  }

  if (controller[`${action}LabelValue`]) {
    button.innerText = controller[`${action}LabelValue`];
  }

  return button;
}

function configureTabulator(controller) {
  Tabulator.extendModule("format", "formatters", {
    has_many_read_only: function (cell, formatterParams) {
      return this.emptyToSpace(this.sanitizeHTML(cell.getValue().join(", ")));
    },
    user_name: function (cell, formatterParams) {
      const container = document.createElement("div");
      container.className = "d-flex align-items-center";

      if (cell.getValue()?.avatar_url) {
        const avatar = document.createElement("img");
        avatar.className = "avatar rounded-circle";
        avatar.alt = "avatar image";
        avatar.setAttribute("src", cell.getValue().avatar_url);
        container.insertAdjacentElement("beforeend", avatar);
      } else {
        const avatar = document.createElement("i");
        avatar.className = "fas fa-user-circle avatar";
        avatar.alt = "avatar image";
        container.insertAdjacentElement("beforeend", avatar);
      }

      const nameContainer = document.createElement("div");
      nameContainer.className = "d-flex flex-column";

      const nameHeading = document.createElement("h6");
      nameHeading.className = "ms-2 mb-0";
      const link = document.createElement("a");
      link.setAttribute("data-turbo-stream", "");
      link.classList.add("d-inline-flex", "flex-row", "align-items-center", "mb-0");
      if (cell.getValue().profile_url) {
        link.href = cell.getValue().profile_url;
      }
      link.textContent = cell.getValue().display_name;
      nameHeading.appendChild(link);
      nameContainer.appendChild(nameHeading);

      container.insertAdjacentElement("beforeend", nameContainer);

      return container;
    },
    row_actions: function (cell, formatterParams, onRendered) {
      const rowActions = {
        history: {
          url: controller[`historyUrlValue`],
        },
        chat: {
          url: controller[`chatUrlValue`],
          action: "click->datagrid#chatRowHandler",
        },
        details: {
          url: controller[`detailsUrlValue`],
        },
        edit: {
          url: controller[`editUrlValue`],
        },
        delete: {
          url: controller[`deleteUrlValue`],
          action: "click->datagrid#deleteRowHandler",
        },
      };
      const actionContainer = document.createElement("div");
      actionContainer.className = "row-actions";

      Object.keys(rowActions).forEach((action) => {
        const args = rowActions[action];
        if (args.url?.length) {
          const button = createActionButton(action, args, cell, controller);
          if (button) {
            actionContainer.insertAdjacentElement("beforeend", button);
          }
        }
      });

      return actionContainer;
    },
  });
}

export default class extends Controller {
  static targets = ["datagrid", "globalSearchInput", "pageCounterString"];
  static values = {
    newUrl: String,
    chatUrl: String,
    columns: Array,
    dateFilter: String,
    datagrid: Object,
    datasourceUrl: String,
    id: String,
    itemsLabel: { type: String, default: "Items" },
    paginationSize: { type: Number, default: 25 },
    rootParam: String,
    showUrl: String,
    historyUrl: String,
    detailsUrl: String,
    detailsLabel: String,
    editUrl: String,
    deleteUrl: String,
    confirmDeleteMessage: String,
    url: String,
  };
  static outlets = ["status-feedback"];

  connect() {
    configureTabulator(this);

    this.boundCreateRowHandler = this.createRowHandler.bind(this);
    this.boundUpdateRowHandler = this.updateRowHandler.bind(this);
    this.boundRemoveRowHandler = this.removeRowHandler.bind(this);
    this.boundPaginationCounterHandler = this.paginationCounterHandler.bind(this);
    this.boundReloadDataHandler = this.reloadDataHandler.bind(this);

    let configs = {
      index: "id",
      height: "60vh",
      popupContainer: true,
      layout: "fitColumns",
      ajaxURL: this.datasourceUrlValue,
      ajaxParams: () => {
        if (this.hasGlobalSearchInputTarget) {
          // Get the value of the search input field
          const searchQuery = this.globalSearchInputTarget.value;

          // Include search_query in params
          return { search_query: searchQuery };
        }
        return {};
      },
      dataReceiveParams: {
        last_page: "total_pages",
        last_row: "total_rows",
      },
      sortMode: "remote",
      filterMode: "remote",
      pagination: true,
      paginationMode: "remote",
      paginationSize: this.paginationSizeValue,
      paginationSizeSelector: true,
      paginationCounter: this.boundPaginationCounterHandler,
      persistence: false,
      movableColumns: false,
      columns: this.columnsValue,
      headerSortElement: "",
      initialSort: [],
    };

    this.datagrid = new Tabulator(this.datagridTarget, configs);

    this.datagrid.on("tableBuilt", () => {
      const headerInputs = this.datagridTarget.querySelectorAll(".tabulator-headers input");
      headerInputs.forEach((input) => {
        const label = input
          .closest(".tabulator-col-content")
          .querySelector(".tabulator-col-title").innerText;
        input.setAttribute("aria-label", label);
      });
    });

    // if (this.hasRowStatsTarget || this.hasDeleteRowButtonTarget) {
    //   this.datagrid.on("rowSelectionChanged", this.boundRowSelectionChangedHandler);
    // }

    document.addEventListener("create-datagrid-row", this.boundCreateRowHandler);
    document.addEventListener("update-datagrid-row", this.boundUpdateRowHandler);
    document.addEventListener("remove-datagrid-row", this.boundRemoveRowHandler);
    document.addEventListener("reload-datagrid-data", this.boundReloadDataHandler);
  }

  disconnect() {
    document.removeEventListener("create-datagrid-row", this.boundCreateRowHandler);
    document.removeEventListener("update-datagrid-row", this.boundUpdateRowHandler);
    document.removeEventListener("remove-datagrid-row", this.boundRemoveRowHandler);
    document.removeEventListener("reload-datagrid-data", this.boundReloadDataHandler);
    this.datagrid.destroy();
  }

  // Create record response handler
  createRowHandler() {
    const { id, data } = event.detail;

    this.datagrid.addRow(data);
  }

  // Update record response handler
  updateRowHandler() {
    const { id, data } = event.detail;

    this.datagrid.updateData([data]);
  }

  // Delete record click handler
  deleteRowHandler(event) {
    event.preventDefault();

    const row = event.target.closest(".tabulator-row");
    const rowData = row ? this.datagrid.getRow(row).getData() : null;

    if (!rowData) {
      console.error("Unable to find row data.");
      return;
    }

    const confirm_message = this.confirmDeleteMessageValue;

    if (confirm(confirm_message)) {
      fetch(this.deleteUrlValue.replace(":id", rowData.id), {
        method: "DELETE",
        headers: this.defaultFetchHeaders(false),
      })
        .then(this.handleResponse)
        .then(this.handleTurboStreamMessage)
        .catch(this.handleResponseError);
    }
  }

  // Delete record response handler
  removeRowHandler() {
    const { ids } = event.detail;

    this.datagrid.deleteRow(ids);

    if (this.hasStatusFeedbackOutlet) {
      this.statusFeedbackOutlet.hide(
        `${ids.length} ${ids.length > 1 ? "records" : "record"} deleted.`,
      );
    }
  }

  searchQueryChangeHandler(event) {
    clearTimeout(this.debounceTimer);

    // Set a new timer
    this.debounceTimer = setTimeout(() => {
      this.datagrid.setData();
    }, 500);
  }

  reloadDataHandler(event) {
    this.datagrid.setData();
  }

  paginationCounterHandler(pageSize, currentRow, currentPage, totalRows, totalPages) {
    return (
      "<span data-datagrid-target='pageCounterString'>Showing " +
      currentRow +
      "-" +
      Math.min(currentRow + pageSize - 1, totalRows) +
      "</span> of " +
      totalRows +
      " " +
      (this.itemsLabelValue ? this.itemsLabelValue : "Rows")
    );
  }

  chatRowHandler(event) {
    event.preventDefault();

    const row = event.target.closest(".tabulator-row");
    const rowData = row ? this.datagrid.getRow(row).getData() : null;

    if (!rowData) {
      console.error("Unable to find row data.");
      return;
    }

    fetch(this.chatUrlValue.replace(encodeURIComponent(":id"), rowData.id), {
      method: "POST",
      headers: this.defaultFetchHeaders(true),
    })
      .then(this.handleResponse)
      .then(this.handleHtmlMessage)
      .catch(this.handleResponseError);
  }

  detailsRowHandler(event) {
    event.preventDefault();

    const row = event.target.closest(".tabulator-row");
    const rowData = row ? this.datagrid.getRow(row).getData() : null;

    if (!rowData) {
      console.error("Unable to find row data.");
      return;
    }

    fetch(this.detailsUrlValue.replace(":id", rowData.id), {
      headers: this.defaultFetchHeaders(false),
    })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  handleResponse(response) {
    if (response.redirected) {
      window.location.href = response.url;
    } else if (response.ok || response.status === 422) {
      return response.text();
    } else {
      console.error("Failed to receive a Turbo Stream response.");
      return Promise.reject(response);
    }
  }
  handleTurboStreamMessage(turboStream) {
    Turbo.renderStreamMessage(turboStream);
  }
  handleHtmlMessage(html) {
    if (html) document.body.innerHTML = html; // Replace full page if needed
  }
  handleResponseError(error) {
    console.error("AJAX Error:", error);
  }

  defaultFetchHeaders(disable_turbo = false) {
    if (disable_turbo) {
      return {
        Accept: "text/html",
        "X-CSRF-Token": document.head.querySelector("meta[name='csrf-token']")?.content,
      };
    } else {
      return {
        "Content-Type": "application/json",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.head.querySelector("meta[name='csrf-token']")?.content,
      };
    }
  }

  // Currently there is no single method to clear filters and sorting so unfortunately two requests may be sent
  clearFiltersHandler() {
    this.globalSearchInputTarget.value = "";
    if (this.datagrid.getSorters().length !== 0) {
      this.datagrid.setSort([]);
    }
    this.datagrid.clearFilter(true);
  }
}
